/**
 * Format utilities for displaying consistent data throughout the application
 */

import { getChannelLabel } from "@/lib/constants/channels";

/**
 * Formats a relation source string into a user-friendly label
 * @param source - The raw relation source string or null
 * @returns Formatted relation source label
 */
export const formatRelationSource = (source: string | null): string => {
  if (!source) return "Auto-detected";

  // Split by comma-space in case there are multiple sources
  const sources = source.split(",");

  // Format each source individually
  const formatted = sources.map((s) => {
    switch (s.toLowerCase().trim()) {
      case "activity_based_healing":
        return "Auto-detected by Upside";
      case "crm_contact_role":
        return "CRM Contact Role";
      case "opportunity_field":
        return "Opportunity Field";
      case "account_field":
        return "Account Field";
      default:
        return s;
    }
  });

  return formatted.join(", ");
};

/**
 * Converts a snake_case string to Title Case with spaces
 * Example: "opportunity_activation" → "Opportunity Activation"
 */
export function formatSnakeCase(text: string): string {
  if (!text) return "";
  return text
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Capitalizes only the first letter while preserving the original casing of the rest
 */
export function capitalizeFirstLetter(text: string): string {
  if (!text) return "";

  // Special case handling for direction values
  const upperText = text.toUpperCase();
  if (upperText === "INBOUND") return "Inbound";
  if (upperText === "OUTBOUND") return "Outbound";
  if (upperText === "UNDETERMINED") return "Undetermined";

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Formats raw touchpoint types from the database to human-readable labels
 * @param touchpointType The raw touchpoint type (e.g., "direct_email", "bulk_email")
 * @returns The formatted label (e.g., "Direct Email", "Bulk Email")
 */
export function formatTouchpointType(touchpointType: string): string {
  if (!touchpointType) return "Unknown";

  // Convert to the expected key format (kebab-case)
  const channelKey = touchpointType.toLowerCase().replace(/[\s_]+/g, "-");

  // Use the shared channel label getter
  return getChannelLabel(channelKey);
}
